import { Card } from '../components/Card';
import { PageHeader } from '../components/PageHeader';
import { ArrowPathIcon, DocumentIcon } from '@heroicons/react/24/outline';
import SortableTable from '../components/SortableTable';
import { Field, Label } from '../catalyst/fieldset';
import { Input } from '../catalyst/input';
import { useState } from 'react';
import { Button } from '../catalyst/button';
import backend from '../services/backend';
import type { ApiEndpoints } from '@avisoma/shared';
import { useToast } from '../components/Toast';
import { Link } from '../components/Link';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import AuthGuard from '../components/AuthGuard';

export default function Debugger() {
  useDocumentTitle('Debugger');
  return (
    <div>
      {/* Header */}
      <PageHeader title="Debugger" description="Debug your application" icon={<DocumentIcon className="size-6" />}>
        <div className="flex items-center gap-4">
          <p className="text-sm text-gray-500">
            {/* {lastFetched && (
                <>Last updated: {lastFetched.toLocaleTimeString()}</>
              )} */}
          </p>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
        <RCEmbeddingMatcher />
        <AuthGuard roles={['admin']}>
          <GrabForeignFiles />
        </AuthGuard>
      </div>
    </div>
  );
}

function GrabForeignFiles() {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ documents: number; pages: number; seconds: number } | null>(null);

  const handleClick = async () => {
    setLoading(true);
    try {
      const { data } = await backend.grabForeignFiles();
      setResult({ documents: data.documents, pages: data.pages, seconds: data.seconds });
      const total = data.documents + data.pages;
      if (total === 0) {
        toast.success('No foreign-bucket files found.');
      } else {
        toast.success(`Enqueued ${data.documents} document(s) and ${data.pages} page(s).`);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to enqueue grab tasks');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card header="Grab Foreign Files">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-500">
          Scan documents and pages with paths outside the project's GCS buckets, and enqueue copy tasks to bring them
          into the local docs/pages bucket.
        </p>
        <div className="flex items-center gap-4">
          <Button onClick={handleClick} disabled={loading}>
            {loading && <ArrowPathIcon className="size-4 mr-2 animate-spin" />}
            Run scan
          </Button>
          {result && (
            <div className="text-sm text-gray-600">
              Matched <strong>{result.documents}</strong> document
              {result.documents === 1 ? '' : 's'} and <strong>{result.pages}</strong> page
              {result.pages === 1 ? '' : 's'} in {result.seconds.toFixed(2)}s.
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function RCEmbeddingMatcher() {
  const [input, setInput] = useState('');
  const [data, setData] = useState<ApiEndpoints<string>['/api/categories/rc-embeddings/match']['POST']['response']>(
    [],
  );
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    backend
      .matchRCEmbeddings(input)
      .then((res) => {
        setData(res.data);
      })
      .catch((e) => {
        toast.error(`Failed to match embeddings: ${e.message}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Card header="RC Embedding Matcher">
      <form className="flex flex-col sm:flex-row items-end gap-4" onSubmit={handleSubmit}>
        <Field className="flex-1">
          <Label>Input </Label>
          <Input value={input} onChange={(e) => setInput(e.target.value)} disabled={loading} />
        </Field>

        <Button type="submit" onClick={() => {}} disabled={loading}>
          {loading && <ArrowPathIcon className="size-4 mr-2 animate-spin" />}
          Match
        </Button>
      </form>

      {data.length > 0 && (
        <SortableTable
          storageId="debugger"
          columns={[
            {
              accessor: 'id',
              label: 'ID',
            },
            {
              accessor: 'name',
              label: 'Name',
              render: (row) => <Link to={`/categories/${row.categoryId}`}>{row.name}</Link>,
            },
            {
              accessor: 'score',
              label: 'Score',
              render: (row) => `${(row.score * 100).toFixed(0)}%`,
            },
            {
              accessor: 'text',
              label: 'Text',
            },
          ]}
          data={data}
          rowKey={(row) => row.id.toString()}
        />
      )}
    </Card>
  );
}
